import React, { useEffect, useState } from 'react';
import { get, getWithParams, post } from "../../utils/fetchAPI";
import PageTitle from "../../components/page-title/pageTitle";
import Loader from "../../components/loader/loder";
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import Select from 'react-select';
import { MdSwapHoriz } from "react-icons/md";
import { FaArrowRight } from 'react-icons/fa';

// Keys must match ENTITY_REGISTRY in the API's adminDataTransferController
const ENTITIES = [
    { key: "advice", label: "Advices" },
    { key: "labTest", label: "Lab Tests" },
    { key: "chiefCompliant", label: "Chief Complaints" },
    { key: "provisionalDx", label: "Provisional Diagnoses" },
    { key: "differentialDx", label: "Differential Diagnoses" },
    { key: "historyOfPresetIllness", label: "History of Present Illness" },
    { key: "physicalExam", label: "Physical Exams", hint: "Same-name exams on the target will be overwritten" },
    { key: "doctorMedicine", label: "Medicines" },
    { key: "followupTest", label: "Follow-up Tests (Investigations Profile)", hint: "Copies belong to the target doctor only. Tests assigned to all doctors are not included" },
    { key: "template", label: "Templates", hint: "Items inside templates are re-linked to the target doctor's own lists" },
];

const DoctorDataTransfer = () => {
    const [doctors, setDoctors] = useState([]);
    const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
    const [sourceDoctor, setSourceDoctor] = useState(null);
    const [targetDoctor, setTargetDoctor] = useState(null);
    const [sourceCounts, setSourceCounts] = useState(null);
    const [targetCounts, setTargetCounts] = useState(null);
    const [isLoadingCounts, setIsLoadingCounts] = useState(false);
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [isCopying, setIsCopying] = useState(false);
    const [copyResults, setCopyResults] = useState(null);

    useEffect(() => {
        fetchDoctors();
    }, []);

    // Refresh source counts and pre-select every entity that has data
    useEffect(() => {
        setCopyResults(null);
        if (!sourceDoctor) {
            setSourceCounts(null);
            setSelectedEntities([]);
            return;
        }
        const loadCounts = async () => {
            setIsLoadingCounts(true);
            const counts = await fetchCounts(sourceDoctor.value);
            setSourceCounts(counts);
            if (counts) {
                setSelectedEntities(ENTITIES.filter((e) => counts[e.key] > 0).map((e) => e.key));
            }
            setIsLoadingCounts(false);
        };
        loadCounts();
    }, [sourceDoctor]);

    useEffect(() => {
        setCopyResults(null);
        if (!targetDoctor) {
            setTargetCounts(null);
            return;
        }
        const loadCounts = async () => {
            setTargetCounts(await fetchCounts(targetDoctor.value));
        };
        loadCounts();
    }, [targetDoctor]);

    const fetchDoctors = async () => {
        setIsLoadingDoctors(true);
        try {
            const response = await getWithParams('/doctor/list-v2', {
                per_page: 100,
                current_page: 1,
            });
            if (response?.status === 200) {
                setDoctors(response.data?.doctors || []);
            }
        } catch (error) {
            console.error('Error fetching doctors:', error);
            toast.error('Failed to fetch doctors list', {
                position: "top-center",
                theme: "light",
            });
        } finally {
            setIsLoadingDoctors(false);
        }
    };

    const fetchCounts = async (doctorId) => {
        try {
            const response = await get(`/doctor-data/counts/${doctorId}`);
            return response?.data?.counts || null;
        } catch (error) {
            console.error('Error fetching doctor data counts:', error);
            toast.error('Failed to fetch doctor data counts', {
                position: "top-center",
                theme: "light",
            });
            return null;
        }
    };

    const handleEntityToggle = (key) => {
        setSelectedEntities((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const selectableEntities = ENTITIES.filter((e) => sourceCounts && sourceCounts[e.key] > 0);
    const allSelected = selectableEntities.length > 0 &&
        selectableEntities.every((e) => selectedEntities.includes(e.key));

    const handleSelectAllToggle = () => {
        setSelectedEntities(allSelected ? [] : selectableEntities.map((e) => e.key));
    };

    const doctorOptions = doctors.map((doctor) => ({ value: doctor._id, label: doctor.name }));
    const targetOptions = doctorOptions.filter((option) => option.value !== sourceDoctor?.value);

    // Render the dropdown menu in a portal: the cards below create their own
    // stacking contexts (drop-shadow), which would otherwise paint over the menu
    const selectMenuProps = {
        menuPortalTarget: document.body,
        styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
    };

    const canCopy = sourceDoctor && targetDoctor &&
        sourceDoctor.value !== targetDoctor.value &&
        selectedEntities.length > 0 && !isCopying;

    const handleCopy = async () => {
        if (!canCopy) return;

        const confirmation = await Swal.fire({
            title: 'Copy doctor data?',
            html: `Copy <b>${selectedEntities.length}</b> data type(s) from <b>${sourceDoctor.label}</b> to <b>${targetDoctor.label}</b>?<br/><br/>
                <small>Entries the target already has will be skipped. Same-name physical exams will be overwritten with the source doctor's version.</small>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, copy!'
        });
        if (!confirmation.isConfirmed) return;

        setIsCopying(true);
        try {
            const response = await post('/doctor-data/copy', {
                sourceDoctorId: sourceDoctor.value,
                targetDoctorId: targetDoctor.value,
                entities: selectedEntities,
            });
            setCopyResults(response?.data?.results || null);
            toast.success('Data copied successfully!', {
                position: "top-center",
                theme: "light",
            });
            // Refresh target counts so "target already has N" reflects the copy
            setTargetCounts(await fetchCounts(targetDoctor.value));
        } catch (error) {
            console.error('Error copying doctor data:', error);
            toast.error(error.response?.data?.errors?.[0]?.msg || 'Failed to copy data', {
                position: "top-center",
                theme: "light",
            });
        } finally {
            setIsCopying(false);
        }
    };

    if (isLoadingDoctors) {
        return (
            <div className="flex justify-center items-center h-full">
                <Loader />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-5 w-full px-2 sm:px-0">
            <PageTitle title="Doctor Data Transfer" icon={<MdSwapHoriz />} />

            {/* Doctor pickers */}
            <div className="bg-white rounded-md p-5 drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)]">
                <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="w-full md:flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Copy from (source doctor)
                        </label>
                        <Select
                            options={doctorOptions}
                            value={sourceDoctor}
                            onChange={(option) => {
                                setSourceDoctor(option);
                                // A doctor can never be their own copy target
                                if (option && targetDoctor?.value === option.value) {
                                    setTargetDoctor(null);
                                }
                            }}
                            placeholder="Select source doctor..."
                            isClearable
                            isSearchable
                            {...selectMenuProps}
                        />
                    </div>
                    <FaArrowRight className="text-primary text-xl shrink-0 hidden md:block mt-5" />
                    <div className="w-full md:flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Copy to (target doctor)
                        </label>
                        <Select
                            options={targetOptions}
                            value={targetDoctor}
                            onChange={setTargetDoctor}
                            placeholder="Select target doctor..."
                            isClearable
                            isSearchable
                            {...selectMenuProps}
                        />
                    </div>
                </div>
            </div>

            {/* Entity selection */}
            {sourceDoctor && (
                <div className="bg-white rounded-md drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)] overflow-hidden">
                    <div className="bg-gradient-to-r from-primary to-teal-600 px-5 py-3 flex justify-between items-center">
                        <h2 className="text-white font-bold text-lg">
                            Data to copy
                            {sourceCounts && (
                                <span className="ml-2 text-sm font-normal opacity-90">
                                    ({selectedEntities.length} of {selectableEntities.length} selected)
                                </span>
                            )}
                        </h2>
                        <button
                            onClick={handleSelectAllToggle}
                            disabled={selectableEntities.length === 0}
                            className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors disabled:opacity-50 ${
                                allSelected
                                    ? 'bg-white text-primary hover:bg-gray-100'
                                    : 'bg-teal-700 text-white hover:bg-teal-800'
                            }`}
                        >
                            {allSelected ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>

                    <div className="p-4">
                        {isLoadingCounts ? (
                            <div className="flex justify-center py-6"><Loader /></div>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {ENTITIES.map((entity) => {
                                    const count = sourceCounts?.[entity.key] ?? 0;
                                    const targetCount = targetCounts?.[entity.key];
                                    const isDisabled = count === 0;
                                    const isSelected = selectedEntities.includes(entity.key);

                                    return (
                                        <label
                                            key={entity.key}
                                            className={`flex items-center gap-3 p-3 rounded-md border-2 transition-all ${
                                                isDisabled
                                                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                                    : isSelected
                                                        ? 'border-primary bg-teal-50 cursor-pointer'
                                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                disabled={isDisabled}
                                                onChange={() => handleEntityToggle(entity.key)}
                                                className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer disabled:cursor-not-allowed"
                                            />
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900">{entity.label}</div>
                                                {entity.hint && !isDisabled && (
                                                    <div className="text-xs text-orange-500">{entity.hint}</div>
                                                )}
                                            </div>
                                            <div className="text-sm text-gray-600 text-right">
                                                <span className="font-medium">{count}</span> item(s)
                                                {targetDoctor && targetCount > 0 && (
                                                    <div className="text-xs text-gray-400">
                                                        target already has {targetCount}
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Copy button */}
            {sourceDoctor && (
                <div className="flex justify-end">
                    <button
                        onClick={handleCopy}
                        disabled={!canCopy}
                        className="px-6 py-2 bg-primary text-white rounded-md hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <MdSwapHoriz className="text-xl" />
                        {isCopying ? 'Copying...' : 'Copy Data'}
                    </button>
                </div>
            )}

            {/* Results */}
            {copyResults && (
                <div className="bg-white rounded-md drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)] overflow-hidden">
                    <div className="bg-gradient-to-r from-primary to-teal-600 px-5 py-3">
                        <h2 className="text-white font-bold text-lg">Copy results</h2>
                    </div>
                    <div className="p-4 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="py-2 pr-4">Data type</th>
                                    <th className="py-2 pr-4 text-right">In source</th>
                                    <th className="py-2 pr-4 text-right">Copied</th>
                                    <th className="py-2 pr-4 text-right">Overwritten</th>
                                    <th className="py-2 text-right">Skipped</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ENTITIES.filter((e) => copyResults[e.key]).map((entity) => {
                                    const result = copyResults[entity.key];
                                    if (result.error) {
                                        return (
                                            <tr key={entity.key} className="border-b bg-red-50 text-red-600">
                                                <td className="py-2 pr-4 font-medium">{entity.label}</td>
                                                <td colSpan={4} className="py-2 text-right">{result.error}</td>
                                            </tr>
                                        );
                                    }
                                    return (
                                        <tr key={entity.key} className="border-b">
                                            <td className="py-2 pr-4 font-medium text-gray-900">{entity.label}</td>
                                            <td className="py-2 pr-4 text-right">{result.sourceTotal}</td>
                                            <td className={`py-2 pr-4 text-right ${result.copied > 0 ? 'text-green-600 font-medium' : ''}`}>
                                                {result.copied}
                                            </td>
                                            <td className={`py-2 pr-4 text-right ${result.updated > 0 ? 'text-orange-500 font-medium' : ''}`}>
                                                {result.updated}
                                            </td>
                                            <td className="py-2 text-right text-gray-500">{result.skipped}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoctorDataTransfer;
